import { diaAr, hoyAr } from "./fechaAr";
import { patente } from "./unidad";

/* La línea «Seguro» de la ficha de una unidad (Aldo, Tráfico, 23/10: «cuando
 * miro un tractor o un semi, que diga si tiene póliza vigente»).
 *
 * ── EL DATO ESTÁ INCOMPLETO, Y LA FRASE TIENE QUE SABERLO ─────────────────
 *
 * `polizas_seguro` tiene 906 filas: 302 de los 710 tractores y 585 de los 766
 * furgones. Que una unidad no tenga fila quiere decir que nadie cargó su
 * póliza, NO que ande sin seguro. Por eso la ficha dice «No hay póliza cargada»
 * y nunca «no asegurada»: la segunda manda a parar un camión por un hueco de la
 * planilla.
 *
 * ── CUATRO CASOS, TRES FRASES ─────────────────────────────────────────────
 *
 *   · `undefined` — no se pudo leer o no hay permiso: la línea NO va. Decir
 *     «no hay póliza» ahí sería contestar algo que no se sabe;
 *   · `null` — se leyó y no hay fila: «No hay póliza cargada…», neutro;
 *   · fila con `vence` de hoy en adelante: vigente hasta esa fecha;
 *   · fila con `vence` pasado: vencida, en ámbar (`aviso`);
 *   · fila con `estado` «fletero»: «Seguro a cargo del fletero», neutro;
 *   · fila con `estado` «baja»: «Sin seguro», en ámbar (`aviso`).
 *
 * ── EL SEGURO DEL FLETERO (Nora, Seguros, 23/10) ──────────────────────────
 *
 * Hay unidades a nombre de la empresa con el seguro tomado por el fletero: no
 * tienen póliza de la empresa y NO andan sin seguro. Va en gris, como «sin
 * cargar», y nunca dice «sin seguro».
 *
 * ── «SIN SEGURO» LO DICE SEGUROS, NO EL HUECO (Nora, 23/10) ───────────────
 *
 * «Si no figuran en el F es porque no tienen seguro»: la unidad que tenía
 * póliza y no está en el Excel de Seguros del semestre, o que Seguros dio por
 * anulada, queda en `estado` «baja». Eso sí es un dato —lo confirmó la
 * responsable— y se dice «Sin seguro», en ámbar. Distinto de no tener fila
 * («No hay póliza cargada», gris): ahí nadie dijo nada.
 *
 * Vence HOY sigue vigente (`vence >= hoy`), igual que `estadoVencimiento` de
 * Seguros, que es el que usan la línea de tiempo y el aviso de arriba. «Hoy»
 * es el argentino (`hoyAr`), no el de la máquina del que mira.
 */

/* La patente como la guarda `polizas_seguro`: mayúsculas, sin espacios ni
 * guiones. Es `patente()` de unidad — el padrón tiene chapas tipeadas «AB 123
 * CD» que comparadas en crudo no encuentran su póliza. */
export const dominioPoliza = (dominio) => patente(dominio);

/* QUIÉN VE LA PÓLIZA, en un solo lugar. Lo usan la ficha de la unidad, la
 * ficha rápida del padrón y la columna «Seguro» de Tractores/Furgones/RTO: si
 * el permiso cambia, cambia acá y las cuatro pantallas se mueven juntas.
 *
 * `permisos`: la lista del usuario, o `null` mientras no llegaron —ahí se
 * muestra, igual que el resto del hub (el rail, `puede` de Unidad). */
/* `ver.polizas`, o Satelital por default: la misma regla que la RLS de
 * `polizas_seguro` y que `PERMISO_POLIZAS` del menú. */
export const PERMISO_SEGURO = ["ver.polizas", "cargar.satelital"];
export const puedeVerSeguro = (permisos) =>
  permisos === null ||
  permisos === undefined ||
  PERMISO_SEGURO.some((p) => permisos.includes(p));

/* La fila es de una unidad con el seguro tomado por el fletero. */
export const esDelFletero = (poliza) => poliza?.estado === "fletero";

/* Seguros dijo que la unidad no tiene seguro (no está en su Excel, o la anuló). */
export const esSinSeguro = (poliza) => poliza?.estado === "baja";

/* `poliza`: la fila (`{ poliza_nro, vence, estado }`), `null` o `undefined` (ver
 * arriba). `hoy`: `YYYY-MM-DD`. Devuelve `{ texto, aviso, neutro }`, o `null`
 * si la línea no va. */
export function lineaSeguro(poliza, hoy = hoyAr()) {
  if (poliza === undefined) return null;
  if (!poliza) {
    return { texto: "No hay póliza cargada para esta unidad", aviso: false, neutro: true };
  }
  if (esDelFletero(poliza)) {
    return { texto: "Seguro a cargo del fletero", aviso: false, neutro: true };
  }
  if (esSinSeguro(poliza)) return { texto: "Sin seguro", aviso: true, neutro: false };
  const nro = String(poliza.poliza_nro ?? "").trim();
  const fecha = diaAr(poliza.vence);
  const cab = nro ? `Póliza ${nro}` : "Póliza sin número";
  /* Sin fecha no se inventa un estado: se dice la póliza y nada más. */
  if (!fecha) return { texto: cab, aviso: false, neutro: false };
  if (String(poliza.vence).slice(0, 10) < hoy)
    return { texto: `${cab} · vencida el ${fecha}`, aviso: true, neutro: false };
  return { texto: `${cab} · vigente hasta ${fecha}`, aviso: false, neutro: false };
}

/* La celda «Seguro» de las tablas de Tractores y Furgones (Aldo, 23/10,
 * ticket 495: «que figure en el FIBOT también cuando se consulta un tractor o
 * un semi»). Lo mismo que `lineaSeguro`, corto para una columna.
 *
 * `polizas`: el `Map` dominio → fila de `usePolizas`, o `undefined` si no se
 * pudo leer — ahí la columna no va, igual que la línea de la ficha. Devuelve
 * `{ valor, texto, aviso, neutro }`: `valor` es por lo que se filtra
 * (Vigente / Vencida / Sin seguro / Del fletero / Sin cargar) y `texto` lo que
 * se ve. */
export function celdaSeguro(polizas, dominio, hoy = hoyAr()) {
  const p = polizas?.get(dominioPoliza(dominio));
  if (!p) return { valor: "Sin cargar", texto: "Sin cargar", aviso: false, neutro: true };
  if (esDelFletero(p))
    return { valor: "Del fletero", texto: "A cargo del fletero", aviso: false, neutro: true };
  if (esSinSeguro(p)) return { valor: "Sin seguro", texto: "Sin seguro", aviso: true, neutro: false };
  const fecha = diaAr(p.vence);
  if (!fecha) return { valor: "Vigente", texto: "Cargada", aviso: false, neutro: false };
  if (String(p.vence).slice(0, 10) < hoy)
    return { valor: "Vencida", texto: `Vencida ${fecha}`, aviso: true, neutro: false };
  return { valor: "Vigente", texto: `Hasta ${fecha}`, aviso: false, neutro: false };
}
